import { useMemo } from "react";

import { mean } from "lodash";

import { useUser } from "@components/user-context";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { MetricValue } from "@shared/metric-value";

import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";
import {
  adjustMetricsGrossRevRow,
  normalizeMetricValue,
  sumCogs,
} from "../../utils";

interface Props {
  metric: SalesPageMetric;
  data: GetOverviewMetricsReport_me_overviewMetricsReport;
}

export const BenchmarkDetails = ({ metric, data }: Props) => {
  const { user } = useUser();
  const { adoptableBenchmarksMetrics, daysSelection, baselineFilters } =
    useUniversalBenchmarksConfiguratorContext();

  const details = useMemo(() => {
    const rowData = data.rows[0];

    const excludedDays = daysSelection
      .filter((e) => e.isExcluded || e.willBeExcluded)
      .map((e) => e.day);

    const metricValues = [];
    const roasValues = [];

    for (const metricPoint of rowData.metrics) {
      if (!excludedDays.includes(metricPoint.date)) {
        metricValues.push(normalizeMetricValue(metric.calculate(metricPoint)));
        roasValues.push(
          normalizeMetricValue(
            adoptableBenchmarksMetrics.roas.calculate(metricPoint),
          ),
        );
      }
    }

    const cogsTotal = sumCogs(baselineFilters.cogs);
    const avgMetricValue = mean(metricValues);
    const avgRoasValue = mean(roasValues);
    const totalDays = rowData.metrics.length;
    let hits = 0;
    let revValue = 0;
    let profitValue = 0;
    let potentialRevValue = 0;
    let potentialProfitValue = 0;

    for (const metricPoint of rowData.metrics) {
      const metricValue = normalizeMetricValue(metric.calculate(metricPoint));
      let hit = false;

      if (metric.isPositiveChangeGood && metricValue >= avgMetricValue) {
        hit = true;
      }

      if (!metric.isPositiveChangeGood && metricValue <= avgMetricValue) {
        hit = true;
      }

      const metricPointGross = adjustMetricsGrossRevRow(
        metricPoint,
        user.ecomAccountingSettings,
      );

      const rev = adoptableBenchmarksMetrics.rev.calculate(metricPointGross);
      const txns = adoptableBenchmarksMetrics.txns.calculate(metricPointGross);
      const profit = rev - txns * cogsTotal;
      revValue += rev;
      profitValue += profit;

      if (hit) {
        hits += 1;
        potentialRevValue += rev;
        potentialProfitValue += profit;
      } else {
        const spend =
          adoptableBenchmarksMetrics.spend.calculate(metricPointGross);
        const potentialRev = spend * avgRoasValue;
        potentialRevValue += potentialRev;
        potentialProfitValue += potentialRev - txns * cogsTotal;
      }
    }

    return {
      avgMetricValue,
      revValue,
      profitValue,
      potentialRevValue,
      potentialProfitValue,
      potentialRevPercent: Math.round((1 - revValue / potentialRevValue) * 100),
      potentialProfitPercent: Math.round(
        (1 - profitValue / potentialProfitValue) * 100,
      ),
      hitRate: Math.round((hits / totalDays) * 100),
    };
  }, [
    data,
    metric,
    adoptableBenchmarksMetrics,
    daysSelection,
    baselineFilters,
    user,
  ]);

  const name =
    metric.metadata.overviewPageMetricName ||
    metric.metadata.name ||
    metric.name;

  return (
    <div className="h-full">
      <h3 className="text-3xl flex items-center gap-1.5 font-bold">
        Your profitable benchmark is {name}{" "}
        <MetricValue
          as="span"
          format={metric.format}
          value={details.avgMetricValue}
          precision="exact"
        />
        .
      </h3>
      <div className="flex-wrap items-center gap-1 text-v2-primary text-xl">
        You’ve hit this benchmark&nbsp;
        <span className="font-semibold">{details.hitRate}%</span>
        &nbsp;of the time, generating&nbsp;
        <MetricValue
          as="span"
          className="font-semibold"
          format="dollars"
          value={details.revValue}
          precision="approximate"
        />
        &nbsp;in revenue and&nbsp;
        <MetricValue
          as="span"
          className="font-semibold"
          format="dollars"
          value={details.profitValue}
          precision="approximate"
        />
        &nbsp;in profit.
        {/* If you always hit your benchmark, your revenue could
        grow to&nbsp;
        <MetricValue
          as="span"
          className="text-v2-action-positive font-semibold"
          format="dollars"
          value={details.potentialRevValue}
          precision="approximate"
        />
        &nbsp;(+{details.potentialRevPercent}%) and profit to&nbsp;
        <MetricValue
          as="span"
          className="text-v2-action-positive font-semibold"
          format="dollars"
          value={details.potentialProfitValue}
          precision="approximate"
        />
        &nbsp;(+{details.potentialProfitPercent}%). */}
      </div>
    </div>
  );
};
